import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类不存在</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={category.name}
        description={category.description}
        keywords={`爆瓜网,${category.name},吃瓜,爆料,黑料,实时更新`}
        canonicalUrl={`https://baogua.com/category/${slug}`}
        ogType="website"
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${category.name} - 爆瓜网`,
            description: category.description,
            url: `https://baogua.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '爆瓜网',
              url: 'https://baogua.com',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baogua.com' },
              { '@type': 'ListItem', position: 2, name: category.name, item: `https://baogua.com/category/${slug}` },
            ],
          },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16 md:pt-20">
          <div className="px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 font-label mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                <span className="text-foreground-800 font-medium">{category.name}</span>
              </nav>
            </div>
          </div>

          <section className="px-4 md:px-6 pb-8 md:pb-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
                <div className="lg:w-3/5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                    <span className="text-xs font-semibold text-foreground-800 font-label tracking-wide">编前语</span>
                  </div>
                  <blockquote className="text-xl md:text-3xl font-heading font-bold text-foreground-950 leading-relaxed">
                    &ldquo;让真相跑赢公关，让爆料回归事实。
                    <br />
                    我们拒绝谣言，只搬运实锤。&rdquo;
                  </blockquote>
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-800">爆瓜编辑部</span>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-verified-badge-fill text-accent-500 text-sm"></i>
                    </span>
                    <span className="text-xs text-foreground-500 font-label">已认证</span>
                  </div>
                </div>
                <div className="lg:w-2/5 flex justify-center">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20investigative%20journalist%20silhouette%20in%20dark%20room%20with%20dramatic%20side%20lighting%2C%20black%20and%20white%20documentary%20style%20portrait%2C%20shoulder%20up%20composition%2C%20looking%20upward%2C%20serious%20expression%2C%20cinematic%20lighting%2C%20clean%20white%20background%2C%20high%20contrast%2C%20editorial%20aesthetic&width=600&height=800&seq=category-portrait-01&orientation=portrait"
                    alt="爆瓜编辑部 - 专业调查记者"
                    title="爆瓜网编辑团队"
                    className="w-40 h-52 md:w-52 md:h-72 object-cover object-top rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8 md:mb-12">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="mt-2 text-sm md:text-base text-foreground-600 font-body">实时更新</p>
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 bg-background-50 cursor-pointer"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={article.thumbnail}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary-100 text-primary-700">
                            {article.categoryName}
                          </span>
                          <span className="text-xs text-foreground-500 font-label">{article.date}</span>
                        </div>
                        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="mt-2 text-xs md:text-sm text-foreground-600 font-body line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500 font-label">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                            {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(0)}万` : article.viewCount}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line w-3.5 h-3.5 flex items-center justify-center"></i>
                            {article.commentCount >= 10000 ? `${(article.commentCount / 10000).toFixed(0)}万` : article.commentCount}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <i className="ri-inbox-line text-4xl text-foreground-400 w-10 h-10 mx-auto flex items-center justify-center mb-3"></i>
                  <p className="text-foreground-600 font-body">该分类暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}